import type { ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface ItemId { id: string }

function SortableCell({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }
  return (
    <div ref={setNodeRef} style={style} className="fc-sgrid__cell">
      <button
        type="button"
        className="fc-sgrid__grip edit-only"
        aria-label="Arrastrar para reordenar"
        {...attributes}
        {...listeners}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/><circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/><circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/></svg>
      </button>
      {children}
    </div>
  )
}

interface Props<T extends ItemId> {
  items: T[]
  onReorder: (ordered: T[]) => void
  className?: string
  children: (item: T) => ReactNode
  /** Nodo extra (no reordenable) al final de la rejilla, p.ej. una celda "+". */
  after?: ReactNode
}

/** Rejilla reordenable por drag&drop (estrategia rect, handle por celda). Paralela
 *  a SortableList pero en CSS grid — usada por el proceso de manipulación. */
export function SortableGrid<T extends ItemId>({ items, onReorder, className, children, after }: Props<T>) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (over && active.id !== over.id) {
      const from = items.findIndex((i) => i.id === active.id)
      const to = items.findIndex((i) => i.id === over.id)
      if (from !== -1 && to !== -1) onReorder(arrayMove(items, from, to))
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
        <div className={className}>
          {items.map((it) => <SortableCell key={it.id} id={it.id}>{children(it)}</SortableCell>)}
          {after}
        </div>
      </SortableContext>
    </DndContext>
  )
}
